import { ACTION_TYPE } from '../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { API_GET_ENTITLEMENT_TYPES } from '../core/redux/api/endpoints';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../core/redux/IAppState';
import ImmutableMap from '../language/ImmutableMap';
import { EntitlementType } from './entitlementType';
import { assoc } from '../language/module';
import { getResource } from '../language/resources/getResource';


const KEY_ACTION = ACTION_TYPE.GET_ALL_ENTITLEMENT_TYPES_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.GET_ALL_ENTITLEMENT_TYPES_REQUEST,
  ACTION_TYPE.GET_ALL_ENTITLEMENT_TYPES_RESPONSE,
  ACTION_TYPE.GET_ALL_ENTITLEMENT_TYPES_ERROR,
];


/**
 * Constructs a redux action that can be dispatched to fetch information about the entitlement types.
 */
export const getEntitlementTypesAction = () => makeApiAction(
  {
    method:   'GET',
    endpoint: API_GET_ENTITLEMENT_TYPES,
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS as RsaaTypes,
    bailout:  (state: IAppState) => !!state.entitlementTypesDetails?.allEntitlementTypes // If we've already loaded entitlement types
                                    || !!state.entitlementTypesDetails?.isLoading // If we're loading entitlement types
                                    || hasTrippedBreaker(state, KEY_ACTION), // If we've tripped the breaker
  },
);


const reducer: ApiReducer<EntitlementType[]> = (state, action, error, isLoading, payload) => {
  if (isLoading) {
    return assoc('entitlementTypesDetails', { isLoading: true, error: undefined }, state);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return assoc(
      'entitlementTypesDetails',
      { isLoading: false, error: getResource(state.locale, 'GET_ENTITLEMENT_TYPES_ACTION_ERROR') },
      withBreaker,
    );
  }

  return assoc(
    'entitlementTypesDetails',
    {
      isLoading:           false,
      error:               undefined,
      allEntitlementTypes: (payload || []).reduce(
        (entitlementTypes, entitlementType) => entitlementTypes.add(
          entitlementType.entitlementTypeId.toString(),
          entitlementType,
        ),
        ImmutableMap.empty<EntitlementType>(),
      ),
    },
    withBreaker,
  );
};


/**
 * Reduces out a new app state from entitlement types actions. These actions load information about all entitlement types
 */
export const getEntitlementTypesReducer = makeApiReducer<EntitlementType[]>(ACTIONS, KEY_ACTION, reducer);
